/* src/views/logements/FicheServicesCtrl.jsx — L'ONGLET « Services » de la
   fiche logement (lot 4, 2026-08-28) : quatre vues du proto (Sur ce
   logement · Le catalogue · Les demandes · Le transfert) + la carte d'état
   et la colonne voyageur.

   ON NE PERD AUCUNE FONCTION :
   · Sur ce logement — toggle + prix personnalisé (sauvegarde au blur,
     vide = retour au prix catalogue, garde F-CRIT-2) + heure de gain des
     deux services horaires (toutes les gardes : badInput protégé, format,
     cohérence avec les horaires standards, valeur illisible avouée) —
     logique portée de ServiceLogementPanel. DÉFAUT CORRIGÉ (revue lot 4) :
     le toggle n'embarque plus la saisie non-enregistrée de l'écran, il
     préserve la valeur serveur.
   · Le catalogue — cartes proto + la MODALE EXISTANTE (ServiceCatalogModal :
     slug, 3 langues auto-traduites, 5 types dont distance base+€/km, stock,
     workflow, désactivation douce). DÉFAUT CORRIGÉ : une erreur ne remplace
     plus l'écran, elle s'affiche en bandeau.
   · Les demandes — 🔴 CHEMIN ARGENT : ServiceBookingsPanel monté TEL QUEL
     (capture/refus/non-livré/hors-plafond Stripe intacts — GO séparé).
   · Le transfert — estimateur distance + « Reporter dans le service »
     (neuf : ouvre la modale du service distance, plus de recopie à la main). */

import { useState, useEffect, useCallback } from 'react'
import { callServicesAction } from '../../lib/api-services-client'
import ServiceCatalogModal from '../../tabs/logements/ServiceCatalogModal'
import ServiceBookingsPanel from '../../tabs/logements/ServiceBookingsPanel'
import { ToggleRond } from './proto/LivretProto'
import { lireAvecCache, invaliderCache } from './ficheCache'

const fmtEuros = (cents) => (Number(cents) / 100).toLocaleString('fr-FR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
const TYPES_LB = { fixed: 'forfait', per_day: 'par jour', per_hour: 'par heure',
  per_person: 'par personne', distance_based: 'selon la distance' }
const ERREURS_FR = {
  max_time_invalid: 'Heure invalide : format attendu HH:mm (ex. 14:00).',
  max_time_not_after_standard: "L'heure doit être APRÈS le départ standard du logement.",
  max_time_not_before_standard: "L'heure doit être AVANT l'arrivée standard du logement.",
  max_time_not_supported_for_service: 'Ce service ne porte pas de limite horaire.',
  standard_time_unresolved: 'Horaires standards introuvables — renseignez-les dans Instructions.',
}
const noteStyle = { fontSize: '10.5px', color: 'var(--gris)', lineHeight: '1.5',
  margin: '8px 0 0' }
const inpServ = { minHeight: '36px', padding: '0 10px', borderRadius: '9px',
  border: '1px solid #d5e3ee', fontSize: '12px', color: 'var(--navy-d)',
  outline: 'none', background: '#fff' }

export default function FicheServicesCtrl({ draft }) {
  const { selected } = draft
  const log = selected.entity
  const [vue, setVue] = useState('actif')
  useEffect(() => { setVue('actif') }, [log.id])

  // ── activations + catalogue (une seule source pour les 4 vues) ────
  const [lignes, setLignes] = useState(null) // services_logement_list enrichi
  const [catalogue, setCatalogue] = useState(null)
  const [erreur, setErreur] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [stats, setStats] = useState({ demandes: 0, acceptees: 0, refusees: 0 })
  const [modale, setModale] = useState(null) // {service}| 'creation'

  const recharger = useCallback(async (frais = false) => {
    setErreur(null)
    try {
      if (frais) {
        invaliderCache(`services:${log.id}`)
        invaliderCache('services-catalogue')
      }
      const [actJson, catJson] = await Promise.all([
        lireAvecCache(`services:${log.id}`, 30000, () =>
          callServicesAction('services_logement_list', {}, {
            method: 'GET', query: { logement_id: log.id } })),
        lireAvecCache('services-catalogue', 60000, () =>
          callServicesAction('services_catalog_list', {}, {
            method: 'GET', requireAuth: false })),
      ])
      setLignes(actJson.services || [])
      setCatalogue(catJson.services || [])
    } catch (e) {
      setErreur(e.message || String(e))
      if (lignes === null) setLignes([])
      if (catalogue === null) setCatalogue([])
    }
    try {
      const bookJson = await callServicesAction('services_bookings_list', {}, {
        method: 'GET', query: { logement_id: log.id, limit: 100 } })
      const bookings = bookJson.bookings || []
      setStats({
        demandes: bookings.length,
        acceptees: bookings.filter((b) => b.status === 'confirmed').length,
        refusees: bookings.filter((b) => ['refunded', 'cancelled'].includes(b.status)).length,
      })
    } catch (e) { console.error('[services] stats demandes', e?.message || e) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log.id])
  useEffect(() => { recharger() }, [recharger])

  const nbProposes = (lignes || []).filter((s) => s.is_active_on_logement).length

  // Le toggle préserve la valeur SERVEUR de l'override — jamais la saisie
  // à l'écran non enregistrée (défaut relevé par l'inventaire, corrigé).
  const basculer = async (s) => {
    setBusyId(s.id); setErreur(null)
    try {
      await callServicesAction('services_logement_toggle', {
        logement_id: log.id, service_catalog_id: s.id,
        is_active: !s.is_active_on_logement,
        price_eur_cents_override: s.price_eur_cents_override ?? null,
      })
      await recharger(true)
    } catch (e) { setErreur(ERREURS_FR[e.message] || e.message || String(e)) } finally { setBusyId(null) }
  }

  const poserOverride = async (s, valeurStr) => {
    const brut = String(valeurStr).trim()
    let override = null
    if (brut !== '') {
      const n = Number(brut.replace(',', '.'))
      if (!Number.isFinite(n) || n < 0) { setErreur('Prix personnalisé invalide.'); return }
      override = Math.round(n * 100)
    }
    if (override === (s.price_eur_cents_override ?? null)) return
    setBusyId(s.id); setErreur(null)
    try {
      await callServicesAction('services_logement_toggle', {
        logement_id: log.id, service_catalog_id: s.id,
        is_active: !!s.is_active_on_logement,
        price_eur_cents_override: override,
      })
      await recharger(true)
    } catch (e) { setErreur(ERREURS_FR[e.message] || e.message || String(e)) } finally { setBusyId(null) }
  }

  const poserMaxTime = async (s, input) => {
    // Saisie incomplète : on restaure la valeur précédente (garde
    // anti-effacement silencieux du panel existant).
    if (input.validity && input.validity.badInput) {
      input.value = s.max_time || ''
      setErreur('Heure incomplète — la limite précédente est conservée.')
      return
    }
    const brut = input.value.trim()
    const max_time = brut === '' ? null : brut
    if ((s.max_time || null) === max_time) return
    if (max_time !== null && !/^\d{2}:\d{2}(:\d{2})?$/.test(max_time)) {
      setErreur('Heure invalide : format attendu HH:mm (ex. 14:00).')
      return
    }
    setBusyId(s.id); setErreur(null)
    try {
      await callServicesAction('services_logement_toggle', {
        logement_id: log.id, service_catalog_id: s.id,
        is_active: !!s.is_active_on_logement,
        price_eur_cents_override: s.price_eur_cents_override ?? null,
        max_time,
      })
      await recharger(true)
    } catch (e) { setErreur(ERREURS_FR[e.message] || e.message || String(e)) } finally { setBusyId(null) }
  }

  // ── l'estimateur (Le transfert) ───────────────────────────────────
  const [depart, setDepart] = useState('')
  const [arrivee, setArrivee] = useState('')
  const [calc, setCalc] = useState(null)
  const [calcBusy, setCalcBusy] = useState(false)
  const [calcErr, setCalcErr] = useState(null)
  const serviceDistance = (catalogue || []).find((s) => s.type === 'distance_based')

  const calculer = async () => {
    setCalcBusy(true); setCalcErr(null); setCalc(null)
    try {
      const json = await callServicesAction('services_distance_estimate', {
        origin: depart.trim(), destination: arrivee.trim(),
        ...(serviceDistance ? { service_catalog_id: serviceDistance.id,
          logement_id: log.id } : {}),
      })
      setCalc(json)
    } catch (e) {
      setCalcErr(String(e.message || e).includes('503')
        ? 'Service Google Maps indisponible (clé API à configurer côté déploiement).'
        : (e.message || String(e)))
    } finally { setCalcBusy(false) }
  }

  const colonneVoyageur = (
    <div className="surOrdi">
      <div style={{ position: 'sticky', top: '14px' }}>
        <p className="inter" style={{ marginTop: '0' }}>Ce que voit le voyageur</p>
        <div style={{ display: 'flex', gap: '9px', background: '#eef2f6',
          border: '1px dashed #c3cdd6', borderRadius: '12px', padding: '10px 11px',
          marginBottom: '10px' }}>
          <span style={{ width: '22px', height: '22px', borderRadius: '6px',
            background: '#fff', border: '1px solid var(--bord)', color: 'var(--gris)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '11px', flexShrink: '0' }}>↻</span>
          <span style={{ flex: '1', minWidth: '0', fontSize: '10.5px',
            color: 'var(--gris-d)', lineHeight: '1.5' }}>L'espace voyageur reste{' '}
            <b>tel qu'il est aujourd'hui</b>. Ce qui suit en montre le contenu.</span>
        </div>
        <div className="carte" style={{ margin: 0 }}>
          <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--navy-d)',
            margin: '0 0 2px' }}>Améliorer votre séjour</p>
          <p style={{ fontSize: '10px', color: 'var(--gris)',
            margin: '0 0 9px' }}>à demander avant votre arrivée</p>
          {(lignes || []).filter((s) => s.is_active_on_logement).map((s, i) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center',
              gap: '9px', padding: '8px 0',
              borderTop: i > 0 ? '1px solid var(--bord)' : 'none' }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 600,
                  color: 'var(--navy-d)' }}>{s.title?.fr || s.slug}</span>
                <span style={{ display: 'block', fontSize: '10px',
                  color: 'var(--gris)', marginTop: '1px' }}>
                  {s.max_time ? `jusqu'à ${String(s.max_time).slice(0, 5).replace(':', 'h')} · ` : ''}
                  {fmtEuros(s.effective_price_eur_cents ?? s.price_eur_cents)}
                  {s.type === 'per_hour' ? " l'heure" : s.type === 'per_day' ? ' par jour' : ''}</span>
              </span>
              <span className="tag t-b">Demander</span>
            </div>
          ))}
          {(lignes || []).filter((s) => s.is_active_on_logement).length === 0 && (
            <p style={{ fontSize: '11px', color: 'var(--gris)', margin: 0 }}>
              Aucun service proposé — le bloc n'apparaît pas au voyageur.</p>
          )}
        </div>
        <p className="note">Les services horaires ne s'affichent que si le ménage
          a le temps de tenir ce jour-là.</p>
      </div>
    </div>
  )

  return (
    <>
      {/* ── la carte d'état ───────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '11px',
        background: '#fff', borderRadius: '14px', padding: '11px 13px',
        marginBottom: '12px',
        boxShadow: '0 1px 3px rgba(35,50,64,.08),0 0 0 1px rgba(35,50,64,.05)' }}>
        <span style={{ width: '34px', height: '34px', borderRadius: '10px',
          background: 'var(--vert-bg)', color: 'var(--vert-tx)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontSize: '15px',
          flexShrink: '0' }}>＋</span>
        <span style={{ flex: '1', minWidth: '0' }}>
          <span style={{ display: 'block', fontSize: '12.5px', fontWeight: '600',
            color: 'var(--navy-d)' }}>{nbProposes} service{nbProposes > 1 ? 's' : ''} proposé{nbProposes > 1 ? 's' : ''}</span>
          <span style={{ display: 'block', fontSize: '10.5px', color: 'var(--gris)',
            marginTop: '1px' }}>sur {(catalogue || []).length} au catalogue</span>
        </span>
        <span style={{ display: 'flex', gap: '13px', flexShrink: '0' }}>
          <span style={{ textAlign: 'right' }}>
            <b style={{ display: 'block', fontSize: '15px', color: 'var(--navy-d)',
              lineHeight: '1' }}>{stats.demandes}</b>
            <span style={{ display: 'block', fontSize: '9px', color: 'var(--gris-d)',
              marginTop: '2px' }}>demandes</span>
          </span>
          <span style={{ textAlign: 'right' }}>
            <b style={{ display: 'block', fontSize: '15px', color: 'var(--navy-d)',
              lineHeight: '1' }}>{stats.acceptees}</b>
            <span style={{ display: 'block', fontSize: '9px', color: 'var(--gris-d)',
              marginTop: '2px' }}>acceptées</span>
          </span>
        </span>
      </div>

      {erreur && (
        <div className="info" data-testid="services-erreur"
          style={{ borderColor: '#e8c4b8', background: '#fbeee9',
            color: '#8a3b28' }}>{erreur}</div>
      )}

      <div className="cols2">
        <div>
          <div style={{ display: 'flex', gap: '3px', background: '#eef2f6',
            borderRadius: '10px', padding: '3px', marginBottom: '12px',
            overflowX: 'auto', scrollbarWidth: 'none' }}>
            {[{ id: 'actif', lb: 'Sur ce logement' },
              { id: 'catalogue', lb: 'Le catalogue' },
              { id: 'resas', lb: 'Les demandes' },
              { id: 'distance', lb: 'Le transfert' }].map((v) => (
              <button key={v.id} onClick={() => setVue(v.id)}
                data-testid={`services-vue-${v.id}`}
                style={vue === v.id
                  ? { flex: '0 0 auto', minHeight: '38px', padding: '0 12px',
                    borderRadius: '8px', fontSize: '11.5px', whiteSpace: 'nowrap',
                    background: '#fff', color: 'var(--navy-d)', fontWeight: 600,
                    boxShadow: '0 1px 3px rgba(35,50,64,.12)', border: 'none',
                    cursor: 'pointer' }
                  : { flex: '0 0 auto', minHeight: '38px', padding: '0 12px',
                    borderRadius: '8px', fontSize: '11.5px', whiteSpace: 'nowrap',
                    color: 'var(--gris)', background: 'transparent', border: 'none',
                    cursor: 'pointer' }}>{v.lb}</button>
            ))}
          </div>

          {/* ═══ Sur ce logement ══════════════════════════════════ */}
          {vue === 'actif' && (
            <>
              <div className="info">Ce qui est proposé sur{' '}
                <b>{log.nom_complet || log.nom || log.id}</b>, et à quel prix. Le
                prix du catalogue s'applique si vous n'en posez pas un autre.</div>
              {lignes === null && (
                <p style={{ fontSize: '11.5px', color: 'var(--gris)' }}>Chargement…</p>
              )}
              {(lignes || []).map((s) => {
                const actif = !!s.is_active_on_logement
                const horaire = ['early_checkin', 'late_checkout'].includes(s.slug)
                const occupe = busyId === s.id
                return (
                  <div className="carte" key={s.id} style={{ margin: '0 0 8px' }}
                    data-testid={`service-ligne-${s.slug}`}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: '12.5px',
                          fontWeight: 600, color: 'var(--navy-d)' }}>{s.title?.fr || s.slug}</span>
                        <span style={{ display: 'block', fontSize: '10.5px',
                          color: actif ? 'var(--vert-tx)' : 'var(--gris)',
                          marginTop: '1px' }}>
                          {actif ? 'proposé au voyageur' : 'pas proposé sur ce logement'}</span>
                      </span>
                      <ToggleRond on={actif} disabled={occupe || !s.is_active}
                        testId={`service-toggle-${s.slug}`}
                        ariaLabel={`Proposer ${s.title?.fr || s.slug} sur ce logement`}
                        onClick={() => basculer(s)} />
                    </div>
                    {!s.is_active && (
                      <p style={noteStyle}>Ce service est éteint au catalogue —
                        rallumez-le d'abord (vue Le catalogue).</p>
                    )}
                    {actif && (
                      <div style={{ paddingTop: '9px', marginTop: '9px',
                        borderTop: '1px solid var(--bord)' }}>
                        <div style={{ display: 'flex', alignItems: 'center',
                          gap: '9px', flexWrap: 'wrap' }}>
                          <span style={{ flex: 1, minWidth: '120px' }}>
                            <span style={{ display: 'block', fontSize: '11px',
                              color: 'var(--gris)' }}>Prix sur ce logement</span>
                            <span style={{ display: 'block', fontSize: '9.5px',
                              color: 'var(--gris-d)', marginTop: '1px' }}>
                              au catalogue : {fmtEuros(s.price_eur_cents)} — vide =
                              ce prix s'applique</span>
                          </span>
                          <input type="number" step="0.01" min="0"
                            data-testid={`service-override-${s.slug}`}
                            defaultValue={s.price_eur_cents_override != null
                              ? (s.price_eur_cents_override / 100).toFixed(2) : ''}
                            key={`${s.id}-${s.price_eur_cents_override ?? 'nul'}`}
                            placeholder={(s.price_eur_cents / 100).toFixed(2)}
                            disabled={occupe}
                            onBlur={(e) => poserOverride(s, e.target.value)}
                            style={{ ...inpServ, width: '90px' }} />
                          <span style={{ fontSize: '11px', color: 'var(--gris)' }}>€</span>
                        </div>
                        {horaire && (
                          <div style={{ display: 'flex', alignItems: 'center',
                            gap: '9px', marginTop: '8px', flexWrap: 'wrap' }}>
                            <span style={{ flex: 1, minWidth: '120px' }}>
                              <span style={{ display: 'block', fontSize: '11px',
                                color: 'var(--gris)' }}>
                                {s.slug === 'late_checkout'
                                  ? 'Départ tardif possible jusqu\'à…'
                                  : 'Arrivée anticipée possible dès…'}</span>
                              <span style={{ display: 'block', fontSize: '9.5px',
                                color: 'var(--gris-d)', marginTop: '1px' }}>vide =
                                aucune limite</span>
                            </span>
                            <input type="time" disabled={occupe}
                              data-testid={`max-time-${s.slug}`}
                              defaultValue={s.max_time ? String(s.max_time).slice(0, 5) : ''}
                              key={`mt-${s.id}-${s.max_time ?? 'nul'}`}
                              onBlur={(e) => poserMaxTime(s, e.target)}
                              style={{ ...inpServ, width: '110px' }} />
                          </div>
                        )}
                        {s.max_time_invalid && (
                          <p style={{ ...noteStyle, color: '#8a3b28' }}>⚠️ Heure
                            enregistrée illisible ({String(s.max_time_raw || '')}) —
                            le service est INDEMANDABLE. Ressaisissez une heure ou
                            videz le champ.</p>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
              <p className="note">{nbProposes} service{nbProposes > 1 ? 's' : ''} proposé{nbProposes > 1 ? 's' : ''} ici.
                Les services horaires n'apparaissent au voyageur que si le
                ménage a le temps de tenir.</p>
            </>
          )}

          {/* ═══ Le catalogue ═════════════════════════════════════ */}
          {vue === 'catalogue' && (
            <>
              <div className="dang">Ce catalogue est <b>partagé par tous les
                logements</b>. Le modifier ici change tous les autres — les prix
                se personnalisent logement par logement, dans la vue d'à côté.</div>
              {(catalogue || []).map((s) => (
                <div className="carte" key={s.id} style={{ margin: '0 0 8px',
                  opacity: s.is_active ? 1 : 0.6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '12.5px',
                        fontWeight: 600, color: 'var(--navy-d)' }}>
                        {s.title?.fr || s.slug}{!s.is_active && ' · éteint'}</span>
                      <span style={{ display: 'block', fontSize: '10.5px',
                        color: 'var(--gris)', marginTop: '1px' }}>
                        {TYPES_LB[s.type] || s.type} · {s.stock_total == null
                          ? 'stock illimité' : `stock ${s.stock_total}`} ·{' '}
                        {s.workflow_validation === 'manual' ? 'validation manuelle' : 'capture directe'}</span>
                    </span>
                    <b style={{ fontSize: '13px', color: 'var(--navy-d)',
                      flexShrink: 0 }}>
                      {s.type === 'distance_based' && s.tarification_metadata
                        ? `${fmtEuros(s.tarification_metadata.base_fee || 0)} + ${fmtEuros(s.tarification_metadata.price_per_km || 0)}/km`
                        : fmtEuros(s.price_eur_cents)}</b>
                  </div>
                  <div className="act" style={{ marginTop: '8px' }}>
                    <button className="bt bt-s" data-testid={`catalogue-modifier-${s.slug}`}
                      onClick={() => setModale({ service: s })}>Modifier</button>
                  </div>
                </div>
              ))}
              {(catalogue || []).length === 0 && catalogue !== null && (
                <p style={{ fontSize: '11.5px', color: 'var(--gris)' }}>Aucun
                  service au catalogue pour l'instant.</p>
              )}
              <div className="act">
                <button className="bt bt-s" data-testid="catalogue-nouveau"
                  onClick={() => setModale('creation')}>＋ Nouveau service</button>
              </div>
              <p className="note">« La limite que vous posez » ne concerne que les
                services facturés à l'heure — elle se règle logement par
                logement, dans la vue d'à côté.</p>
            </>
          )}

          {/* ═══ Les demandes — 🔴 CHEMIN ARGENT (panneau intact) ══ */}
          {vue === 'resas' && (
            <>
              <div className="grid g3" style={{ marginBottom: '12px' }}>
                {[[stats.demandes, 'Demandées', '#fff'],
                  [stats.acceptees, 'Acceptées', 'var(--vert-bg)'],
                  [stats.refusees, 'Refusées', '#fff']].map(([n, lb, fond]) => (
                  <div className="stat" key={lb} style={{ background: fond }}>
                    <b style={{ color: 'var(--gris-d)' }}>{n}</b>
                    <s style={{ color: 'var(--gris-d)' }}>{lb}</s>
                  </div>
                ))}
              </div>
              {/* Valider = capture Stripe · Refuser = remboursement · hors
                  plafond = lien de paiement au montant recalculé (409 si
                  changé). Panneau existant monté tel quel — zéro réécriture
                  du chemin argent. */}
              <ServiceBookingsPanel logement={log} />
            </>
          )}

          {/* ═══ Le transfert ═════════════════════════════════════ */}
          {vue === 'distance' && (
            <>
              <div className="info">Le transfert se facture <b>un forfait plus
                un prix au kilomètre</b>. Calculez la distance, et le prix à
                annoncer se déduit.</div>
              <div className="carte" style={{ margin: '0 0 9px' }}>
                <p style={{ fontSize: '11px', color: 'var(--gris)',
                  margin: '0 0 5px' }}>Départ</p>
                <input value={depart} onChange={(e) => setDepart(e.target.value)}
                  maxLength={200} data-testid="transfert-depart"
                  placeholder="Ex : Aéroport de Toulouse-Blagnac"
                  style={{ ...inpServ, width: '100%', boxSizing: 'border-box',
                    marginBottom: '8px' }} />
                <p style={{ fontSize: '11px', color: 'var(--gris)',
                  margin: '0 0 5px' }}>Arrivée</p>
                <input value={arrivee} onChange={(e) => setArrivee(e.target.value)}
                  maxLength={200} data-testid="transfert-arrivee"
                  placeholder={log.adresse_complete || 'Adresse du logement'}
                  style={{ ...inpServ, width: '100%', boxSizing: 'border-box' }} />
                <div className="act">
                  <button className="bt bt-p" data-testid="transfert-calculer"
                    disabled={calcBusy || !depart.trim() || !arrivee.trim()}
                    onClick={calculer}>
                    {calcBusy ? 'Calcul en cours…' : 'Calculer la distance'}</button>
                </div>
                {calcErr && (
                  <p style={{ ...noteStyle, color: '#8a3b28' }}>{calcErr}</p>
                )}
              </div>
              {calc && (
                <div className="carte" style={{ margin: '0', background: '#3d5166' }}
                  data-testid="transfert-resultat">
                  <p style={{ fontSize: '10px', color: 'rgba(255,255,255,.7)',
                    margin: '0 0 3px', textTransform: 'uppercase',
                    letterSpacing: '.05em' }}>Le prix à annoncer</p>
                  {calc.price_estimate ? (
                    <p style={{ fontSize: '22px', fontWeight: 600, color: '#fff',
                      margin: '0 0 8px' }}>{fmtEuros(calc.price_estimate.amount_eur_cents)}</p>
                  ) : (
                    <p style={{ fontSize: '12px', color: '#fff',
                      margin: '0 0 8px' }}>distance seule — aucun service distance
                      au catalogue</p>
                  )}
                  <p style={{ fontSize: '11px', color: 'rgba(255,255,255,.8)',
                    margin: 0 }}>{calc.distance_km} km · ≈ {calc.duration_minutes} min</p>
                  {serviceDistance && (
                    <div className="act">
                      <button className="bt bt-p" data-testid="transfert-reporter"
                        onClick={() => setModale({ service: serviceDistance })}>
                        Reporter dans le service</button>
                    </div>
                  )}
                </div>
              )}
              {calc && (
                <p className="note">Sans ce report, il faudrait recopier le
                  chiffre à la main — « Reporter » ouvre le service transfert
                  pour ajuster son forfait et son prix au kilomètre.</p>
              )}
            </>
          )}
        </div>
        {colonneVoyageur}
      </div>

      {modale && (
        <ServiceCatalogModal
          service={modale === 'creation' ? null : modale.service}
          onClose={() => setModale(null)}
          onSaved={() => { setModale(null); recharger(true) }} />
      )}
    </>
  )
}
